import type { Profile } from "@/features/profiles";
import { LOCALE_KEY } from "@/lib/local-storage.consts";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDebounceValue, useLocalStorage } from "usehooks-ts";

import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSet,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ProfileAvatar, useDeleteAvatarMutation } from "@/features/avatar";
import {
  useProfileQuery,
  useProfileSubscription,
  useUpdateAvatarUrlMutation,
  useUpsertProfileMutation,
} from "@/features/profiles";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import { getInitials } from "@/features/avatar/avatar.utils";

interface SaveStatus {
  status: "idle" | "saving" | "saved" | "error";
  message?: string;
}

export const AVATARS_STORAGE_BUCKET = "avatars";

const languages = [
  { code: "en", displayName: "English" },
  { code: "de", displayName: "Deutsch" },
  { code: "fr", displayName: "Français" },
];

export default function ProfileSettings({ userId }: { userId: string }) {
  const [saveStatus, setSaveStatus] = useState<SaveStatus>({ status: "idle" });
  const [localProfile, setLocalProfile] = useState<Profile | null>(null);
  const isInitializedRef = useRef(false);
  const isSavingRef = useRef(false);
  const [, setLocale] = useLocalStorage<string>(LOCALE_KEY, "en");

  // Use Supabase cache helpers for profile data
  const { data: profile, isLoading: loading, error: profileError } = useProfileQuery(userId);

  // Real-time subscription for profile updates
  useProfileSubscription(userId);

  // Cache helper mutations
  const upsertProfileMutation = useUpsertProfileMutation();
  const updateAvatarUrlMutation = useUpdateAvatarUrlMutation();
  const deleteAvatarMutation = useDeleteAvatarMutation();

  const saveProfile = useCallback(
    (updatedProfile: Profile) => {
      if (isSavingRef.current) return;
      isSavingRef.current = true;
      setSaveStatus({ status: "saving" });

      upsertProfileMutation.mutate(
        {
          ...updatedProfile,
          updated_at: new Date().toISOString(),
        },
        {
          onSuccess: () => {
            isSavingRef.current = false;
            setSaveStatus({ status: "saved" });
            setTimeout(() => {
              setSaveStatus({ status: "idle" });
            }, 2000);
          },
          onError: (err) => {
            isSavingRef.current = false;
            console.error("Save failed:", err);
            const message = err instanceof Error ? err.message : "Unknown error occurred";
            setSaveStatus({
              status: "error",
              message: `Failed to save: ${message}`,
            });
          },
        },
      );
    },
    [upsertProfileMutation],
  );

  // Initialize local profile when profile loads
  useEffect(() => {
    if (profile && !isInitializedRef.current) {
      setLocalProfile(profile);
      isInitializedRef.current = true;
    }
  }, [profile]);

  // Use debounced value instead of debounced callback
  const [debouncedProfile] = useDebounceValue(localProfile, 300);

  // Save when debounced profile changes
  useEffect(() => {
    // Only save if we've initialized and the debounced profile is different
    if (isInitializedRef.current && debouncedProfile && profile) {
      // Check if any field has actually changed
      const hasChanges =
        debouncedProfile.first_name !== profile.first_name ||
        debouncedProfile.last_name !== profile.last_name ||
        debouncedProfile.locale !== profile.locale ||
        debouncedProfile.notification_preferences.invitations !==
          profile.notification_preferences.invitations ||
        debouncedProfile.notification_preferences.mentions !==
          profile.notification_preferences.mentions;

      if (hasChanges) {
        saveProfile(debouncedProfile);
      }
    }
  }, [isInitializedRef, debouncedProfile, profile, saveProfile]);

  const updateProfileField = useCallback(
    (field: keyof Profile, value: string) => {
      if (!profile) return;

      const updatedProfile = { ...profile, [field]: value };
      setLocalProfile(updatedProfile);
    },
    [profile],
  );

  const updateNotificationPreference = useCallback(
    (key: "invitations" | "mentions", value: boolean) => {
      if (!localProfile) return;
      setLocalProfile({
        ...localProfile,
        notification_preferences: {
          ...localProfile.notification_preferences,
          [key]: value,
        },
      });
    },
    [localProfile],
  );

  const handleLocaleChange = useCallback(
    (code: string) => {
      setLocale(code);
      if (!localProfile) return;
      setLocalProfile({ ...localProfile, locale: code });
    },
    [localProfile, setLocale],
  );

  const handleAvatarUpload = (avatarUrl: string) => {
    if (!profile) return;

    setSaveStatus({ status: "saving" });

    updateAvatarUrlMutation.mutate(
      {
        userId,
        avatarUrl,
      },
      {
        onSuccess: () => {
          setSaveStatus({ status: "saved" });
          setTimeout(() => {
            setSaveStatus({ status: "idle" });
          }, 2000);
        },
        onError: (err) => {
          console.error("Avatar upload failed:", err);
          const message = err instanceof Error ? err.message : "Unknown error occurred";
          setSaveStatus({
            status: "error",
            message: `Failed to update avatar: ${message}`,
          });
        },
      },
    );
  };

  const handleAvatarDelete = () => {
    if (!profile) return;

    setSaveStatus({ status: "saving" });

    // Delete the file from storage if it exists
    if (profile.avatar_url) {
      deleteAvatarMutation.mutate(
        { storageName: AVATARS_STORAGE_BUCKET, filePath: profile.avatar_url },
        {
          onError: (deleteError) => {
            console.warn("Failed to delete avatar file:", deleteError);
            // Continue with profile update even if file deletion fails
          },
        },
      );
    }

    // Update profile to remove avatar_url
    updateAvatarUrlMutation.mutate(
      {
        userId,
        avatarUrl: null,
      },
      {
        onSuccess: () => {
          setSaveStatus({ status: "saved" });
          setTimeout(() => {
            setSaveStatus({ status: "idle" });
          }, 2000);
        },
        onError: (err) => {
          console.error("Avatar delete failed:", err);
          const message = err instanceof Error ? err.message : "Unknown error occurred";
          setSaveStatus({
            status: "error",
            message: `Failed to delete avatar: ${message}`,
          });
        },
      },
    );
  };

  const getSaveStatusIndicator = () => {
    switch (saveStatus.status) {
      case "saving":
        return <span className="text-sm text-blue-600">Saving...</span>;
      case "saved":
        return <span className="text-sm text-green-600">Saved</span>;
      case "error":
        return <span className="text-sm text-red-600">Error saving</span>;
      default:
        return null;
    }
  };

  if (loading) return <div>Loading...</div>;
  if (profileError) return <div>Error loading profile: {profileError.message}</div>;
  if (!profile) return <div>No profile found</div>;

  return (
    <div className="space-y-8">
      {getSaveStatusIndicator() ? (
        <div className="flex justify-end">{getSaveStatusIndicator()}</div>
      ) : null}
      {saveStatus.status === "error" && !!saveStatus.message && (
        <Alert variant="destructive">
          <AlertDescription>{saveStatus.message}</AlertDescription>
        </Alert>
      )}
      <FieldSet>
        <FieldLegend>Profile</FieldLegend>
        <FieldGroup>
          <Field className="gap-2">
            <FieldLabel htmlFor="email">Email</FieldLabel>
            <Input id="email" type="text" value={profile.email || ""} disabled />
          </Field>

          <div className="grid gap-4 lg:grid-cols-2">
            <Field className="gap-2">
              <FieldLabel htmlFor="first_name">First name</FieldLabel>
              <Input
                id="first_name"
                type="text"
                value={localProfile?.first_name ?? ""}
                onChange={(e) => updateProfileField("first_name", e.target.value)}
              />
            </Field>

            <Field className="gap-2">
              <FieldLabel htmlFor="last_name">Last name</FieldLabel>
              <Input
                id="last_name"
                type="text"
                value={localProfile?.last_name ?? ""}
                onChange={(e) => updateProfileField("last_name", e.target.value)}
              />
            </Field>
          </div>

          <Field>
            <div className="flex items-center gap-4">
              <ProfileAvatar
                fallbackInitials={getInitials({
                  firstName: profile.first_name,
                  lastName: profile.last_name,
                  email: profile.email,
                })}
                avatarPath={profile.avatar_url}
                size={100}
                storageName={AVATARS_STORAGE_BUCKET}
                onUpload={handleAvatarUpload}
                onDelete={handleAvatarDelete}
                editable
              />
              <div className="flex h-full flex-col justify-between">
                <FieldLabel>Avatar</FieldLabel>
                <span className="text-muted-foreground text-xs">
                  PNGs and JPEGs under 5MB supported.
                </span>
              </div>
            </div>
          </Field>
        </FieldGroup>
      </FieldSet>

      <Separator />

      <FieldSet>
        <FieldLegend>Language</FieldLegend>
        <FieldGroup>
          <Field className="gap-2">
            <Select value={localProfile?.locale ?? "en"} onValueChange={handleLocaleChange}>
              <SelectTrigger id="locale">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {languages.map((lang) => (
                  <SelectItem key={lang.code} value={lang.code}>
                    {lang.displayName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        </FieldGroup>
      </FieldSet>

      <Separator />

      <FieldSet>
        <FieldLegend>Email notifications</FieldLegend>
        <FieldGroup>
          <div>
            <Field orientation="horizontal">
              <Checkbox
                id="notifications-invitations"
                checked={localProfile?.notification_preferences.invitations ?? true}
                onCheckedChange={(checked) =>
                  updateNotificationPreference("invitations", checked === true)
                }
              />
              <FieldLabel htmlFor="notifications-invitations" className="pb-px">
                Invitations
              </FieldLabel>
            </Field>
            <FieldDescription className="pl-7">
              When someone invites you to a workspace or project.
            </FieldDescription>
          </div>

          <div>
            <Field orientation="horizontal">
              <Checkbox
                id="notifications-mentions"
                checked={localProfile?.notification_preferences.mentions ?? true}
                onCheckedChange={(checked) =>
                  updateNotificationPreference("mentions", checked === true)
                }
              />
              <FieldLabel htmlFor="notifications-mentions" className="pb-px">
                Mentions
              </FieldLabel>
            </Field>
            <FieldDescription className="pl-7">
              When someone mentions you in a chat or document.
            </FieldDescription>
          </div>
        </FieldGroup>
      </FieldSet>
    </div>
  );
}
